import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";
import { PresetController } from "../helper/controllers/preset-controller";

export type SkyFlexboxDirection = "row" | "row-reverse" | "column" | "column-reverse";
export type SkyFlexboxWrap = "nowrap" | "wrap" | "wrap-reverse";
export type SkyFlexboxJustify =
  | "flex-start"
  | "center"
  | "flex-end"
  | "space-between"
  | "space-around"
  | "space-evenly";
export type SkyFlexboxAlign = "stretch" | "flex-start" | "center" | "flex-end" | "baseline";
export type SkyFlexboxAlignContent =
  | "normal"
  | "stretch"
  | "center"
  | "start"
  | "end"
  | "flex-start"
  | "flex-end"
  | "space-between"
  | "space-around"
  | "space-evenly";

/**
 * @element sky-flexbox
 *
 * @summary Flexbox container with grid-like span support for slotted children.
 *
 * @status stable
 * @since 1.0.0
 *
 * @documentation https://sky-ui.com/components/flexbox
 *
 * @slot - Default slot for flex items (`<sky-flexcol>` or plain elements with `span` attributes).
 *
 * @property {SkyFlexboxDirection} direction - Flex direction.
 * @property {SkyFlexboxWrap} wrap - Flex wrap behavior.
 * @property {SkyFlexboxJustify} justify - Main-axis alignment.
 * @property {SkyFlexboxAlign} align - Cross-axis alignment.
 * @property {SkyFlexboxAlignContent} alignContent - Multi-line cross-axis distribution.
 * @property {string} gap - Shared row/column gap value.
 * @property {string | undefined} gapX - Horizontal gap override.
 * @property {string | undefined} gapY - Vertical gap override.
 * @property {number} col - Total column count used for span calculations.
 * @property {string} preset - Named prop preset from nearest `sky-config-provider`. Default: `""`.
 *
 * @fires {Event} slotchange - Fired when slotted children change.
 * @method recalculateSpans Recomputes slotted child span CSS variables.
 *
 * @csspart container - The flex container element.
 *
 * @Behavior
 * - Implements CSS Flexbox with custom property-based configuration
 * - Supports responsive span calculations based on column grid system
 * - Automatically manages CSS custom properties for child elements
 * - Handles responsive breakpoints through span attributes
 *
 * - Uses a configurable column count (default: 12)
 * - Calculates item widths based on span values (1-col through total columns)
 * - Supports responsive spans: `span-lg`, `span-md`, `span-sm`, `span-xs`
 * - Clamps span values to valid range (1 to column count)
 *
 * - Supports unified `gap` property
 * - Optional `gapX` and `gapY` for separate horizontal/vertical spacing
 * - Gap values can use any CSS unit (px, rem, em, %, etc.)
 *
 * @example
 * ```html
 * <sky-flexbox col="12" gap="16px">
 *   <sky-flexcol span="8" span-md="12">
 *     Main content
 *   </sky-flexcol>
 *   <sky-flexcol span="4" span-md="12">
 *     Sidebar
 *   </sky-flexcol>
 * </sky-flexbox>
 * ```
 * ```vue
 * <template>
 *   <sky-flexbox col="12" gap="16px">
 *     <sky-flexcol span="8" span-md="12">Main content</sky-flexcol>
 *     <sky-flexcol span="4" span-md="12">Sidebar</sky-flexcol>
 *   </sky-flexbox>
 * </template>
 * ```
 * ```jsx
 * export default function Demo() {
 *   return (
 *     <sky-flexbox col="12" gap="16px">
 *       <sky-flexcol span="8" span-md="12">Main content</sky-flexcol>
 *       <sky-flexcol span="4" span-md="12">Sidebar</sky-flexcol>
 *     </sky-flexbox>
 *   );
 * }
 * ```
 */
@customElement("sky-flexbox")
export class SkyFlexbox extends LitElement {
  /**
   * Flex direction.
   * @public
   */
  @property({ type: String })
  direction: SkyFlexboxDirection = "row";

  /**
   * Wrap behavior.
   * @public
   */
  @property({ type: String })
  wrap: SkyFlexboxWrap = "wrap";

  /**
   * Main axis justification.
   * @public
   */
  @property({ type: String })
  justify: SkyFlexboxJustify = "flex-start";

  /**
   * Cross axis alignment.
   * @public
   */
  @property({ type: String })
  align: SkyFlexboxAlign = "stretch";

  /**
   * Multi-line alignment (when wrapping).
   * @public
   */
  @property({ type: String })
  alignContent: SkyFlexboxAlignContent = "normal";

  /**
   * Shorthand gap; used if gapX/gapY not provided.
   * @public
   */
  @property({ type: String }) gap: string = "16px";

  /**
   * Column gap.
   * @public
   */
  @property({ type: String })
  gapX?: string;

  /**
   * Row gap.
   * @public
   */
  @property({ type: String })
  gapY?: string;

  /**
   * Total columns used for span calculation.
   * @public
   */
  @property({ type: Number }) col: number = 12;


  /** Named prop preset from nearest `sky-config-provider`. */
  @property({ type: String, reflect: true }) preset = "";

  private _presets = new PresetController(this);

  static override styles = css`
    :host {
      display: block;
    }

    .flex-container {
      display: flex;
      box-sizing: border-box;
      flex-wrap: var(--wrap, wrap);
      gap: var(--gap-y, var(--gap, 16px)) var(--gap-x, var(--gap, 16px));
      justify-content: var(--justify, flex-start);
      align-items: var(--align, stretch);
      align-content: var(--align-content, normal);
      flex-direction: var(--direction, row);
    }

    /* Default behaviour for any slotted child: */
    ::slotted(*) {
      box-sizing: border-box;
      min-width: 0;
    }

    .flex-container.is-row ::slotted(*) {
      flex-grow: 0;
      flex-shrink: 1;
      flex-basis: auto;
      max-width: none;
    }

    .flex-container.is-row ::slotted([data-flex-span]) {
      flex-shrink: 0;
      --span-current: var(--span, 1);
      --span-width: calc(
        100% * var(--span-current) / var(--columns, 12) -
          var(--gap-x, var(--gap, 16px)) *
          (var(--columns, 12) - var(--span-current)) / var(--columns, 12)
      );

      flex-basis: var(--span-width);
      width: var(--span-width);
      max-width: var(--span-width);
    }

    .flex-container.is-column ::slotted(*) {
      width: 100%;
      flex-basis: auto;
      max-width: none;
      flex-shrink: 0;
    }

    @media (max-width: 1200px) {
      .flex-container.is-row ::slotted([data-flex-span]) {
        --span-current: var(--span-lg, var(--span-current));
      }
    }

    @media (max-width: 992px) {
      .flex-container.is-row ::slotted([data-flex-span]) {
        --span-current: var(--span-md, var(--span-current));
      }
    }

    @media (max-width: 768px) {
      .flex-container.is-row ::slotted([data-flex-span]) {
        --span-current: var(--span-sm, var(--span-current));
      }
    }

    @media (max-width: 576px) {
      .flex-container.is-row ::slotted([data-flex-span]) {
        --span-current: var(--span-xs, var(--span-current));
      }
    }
  `;

  /** @private */
  private _childMo?: MutationObserver;
  private _spansRaf = 0;

  /** @protected */
  override connectedCallback() {
    super.connectedCallback();
    this._childMo = new MutationObserver(() => this._scheduleUpdateSpans());
    this._childMo.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["span", "span-lg", "span-md", "span-sm", "span-xs"],
    });
  }

  /** @protected */
  override disconnectedCallback() {
    super.disconnectedCallback();
    if (this._spansRaf) {
      cancelAnimationFrame(this._spansRaf);
      this._spansRaf = 0;
    }
    this._childMo?.disconnect();
  }

  /** @protected */
  override firstUpdated() {
    this.syncVars();
    this.updateSpans(); // initial
  }

  /** @protected */
  override updated(changed: Map<string | number | symbol, unknown>) {
    if (
      changed.has("wrap") ||
      changed.has("gap") ||
      changed.has("gapX") ||
      changed.has("gapY") ||
      changed.has("col") ||
      changed.has("justify") ||
      changed.has("direction") ||
      changed.has("align") ||
      changed.has("alignContent")
    ) {
      this.syncVars();
    }
    if (changed.has("col")) {
      this.updateSpans(); // re-validate span bounds when column count changes
    }
  }

  /**
   * Syncs component properties to CSS custom properties.
   * @private
   */
  private syncVars() {
    const styles = {
      "--wrap": this.wrap,
      "--gap": this.gap,
      "--gap-x": this.gapX ?? this.gap,
      "--gap-y": this.gapY ?? this.gap,
      "--columns": String(this.col),
      "--justify": this.justify,
      "--direction": this.direction,
      "--align": this.align,
      "--align-content": this.alignContent,
    } as Record<string, string>;
    Object.entries(styles).forEach(([k, v]) => this.style.setProperty(k, v));
  }

  /**
   * Forces recalculation of spans for all child elements.
   * Useful when dynamically adding/removing items.
   * @public
   */
  public recalculateSpans() {
    this.updateSpans();
  }

  /** @protected */
  override render() {
    const isColumn =
      this.direction === "column" || this.direction === "column-reverse";

    return html`
      <div
        class="flex-container ${isColumn ? "is-column" : "is-row"}"
        part="container"
      >
        <slot @slotchange=${this.updateSpans}></slot>
      </div>
    `;
  }

  private _scheduleUpdateSpans = () => {
    if (this._spansRaf) return;
    this._spansRaf = requestAnimationFrame(() => {
      this._spansRaf = 0;
      this.updateSpans();
    });
  };

  /**
   * Reads span attributes off slotted children and maps them to CSS vars:
   * span, span-lg, span-md, span-sm, span-xs
   * @private
   */
  private updateSpans = () => {
    const nodes =
      this.shadowRoot
        ?.querySelector("slot")
        ?.assignedElements({ flatten: true }) ?? [];
    for (const el of nodes) {
      if (!(el instanceof HTMLElement)) continue;

      this.applySpanVar(el, "--span", this.readSpanRaw(el, "span", "span"));
      this.applySpanVar(el, "--span-lg", this.readSpanRaw(el, "span-lg", "spanLg"));
      this.applySpanVar(el, "--span-md", this.readSpanRaw(el, "span-md", "spanMd"));
      this.applySpanVar(el, "--span-sm", this.readSpanRaw(el, "span-sm", "spanSm"));
      this.applySpanVar(el, "--span-xs", this.readSpanRaw(el, "span-xs", "spanXs"));

      const hasSpan =
        this.readSpanRaw(el, "span", "span") != null ||
        this.readSpanRaw(el, "span-lg", "spanLg") != null ||
        this.readSpanRaw(el, "span-md", "spanMd") != null ||
        this.readSpanRaw(el, "span-sm", "spanSm") != null ||
        this.readSpanRaw(el, "span-xs", "spanXs") != null;

      if (hasSpan) el.dataset.flexSpan = "";
      else delete el.dataset.flexSpan;
    }
  };

  /**
   * Reads a span value from an HTML attribute or element property.
   * @private
   */
  private readSpanRaw(
    el: HTMLElement,
    attrName: string,
    propName: string,
  ): string | null {
    const fromAttr = el.getAttribute(attrName);
    if (fromAttr != null && fromAttr !== "") return fromAttr;
    const fromProp = (el as unknown as Record<string, unknown>)[propName];
    if (fromProp != null && fromProp !== "") return String(fromProp);
    return null;
  }

  /**
   * Sets or clears a span CSS variable on a slotted child.
   * @private
   */
  private applySpanVar(
    el: HTMLElement,
    cssVar: string,
    raw: string | null,
  ): void {
    const value = this.clampSpan(raw);
    if (value) el.style.setProperty(cssVar, String(value));
    else el.style.removeProperty(cssVar);
  }

  /**
   * Clamps span value to valid range (1 to column count).
   * @param raw - Raw span value from attribute
   * @returns Clamped number or null if invalid
   * @private
   */
  private clampSpan(raw: string | null): number | null {
    if (!raw) return null;
    const n = Math.max(1, Math.min(this.col, Number(raw) || 1));
    return n;
  }
}
